import { type FC, memo, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { type PlantType } from '@common/types/common';
import { type BoostCardProps } from '@common/types/components';
import { type FactoryTypeText } from '@common/types/factory';
import { type PlantTypeText } from '@common/types/plant';
import { type ResourceTypeText } from '@common/types/resource';
import { BoostCard } from '@components/BoostCard/BoostCard';
import { CoinBalanceTopUp } from '@components/CoinBalanceTopUp/CoinBalanceTopUp';
import {
  H2,
  H3,
  StringMain,
  StringSmall,
  StringXSmall,
} from '@components/Typography/Typography';
import { Close } from '@components/ui/Close/Close';
import cn from 'classnames';

import { resourceList } from '@/common/constants';
import { getImage } from '@/common/lib/images';
import { getText } from '@/common/lib/text';
import { type GoodsType } from '@/common/types/goods';
import { PurpleSmButton } from '@/components/ui/Button/Button';
import { CurrencyImage } from '@/components/ui/CurrencyImage/CurrencyImage';
import { Picture } from '@/components/ui/Picture/Picture';

import HelpIcon from '../../HelpModal/HelpIcon';
import { ModalContext } from '../../Modal/config';

import css from './styles.module.scss';

interface BoostCardListProps {
  forCurrentPlantList: Omit<
    BoostCardProps,
    'resource' | 'plantType' | 'plantId'
  >[];
  forAllPlantlist: Omit<BoostCardProps, 'resource' | 'plantType' | 'plantId'>[];
  totalBoost?: number;
  plantId: string;
  plantType: PlantType;
  plant: PlantTypeText | ResourceTypeText | FactoryTypeText;
  level: number;
  buildQuantity?: number;
  item: PlantTypeText | ResourceTypeText | GoodsType;
}

const BoostCardList: FC<BoostCardListProps> = memo(
  ({
    forAllPlantlist,
    forCurrentPlantList,
    totalBoost,
    plantType,
    plantId,
    plant,
    item,
  }) => {
    const { closeModal, openModal, boostProductionDynamicData } =
      useContext(ModalContext);
    const { t } = useTranslation();

    const getBoostDescription = (plantType: PlantType): string => {
      switch (plantType) {
        case 'Quarry': {
          return t('All Extractors');
        }
        case 'Factory': {
          return t('All Plant');
        }
        case 'Workshop': {
          return t('All Factory');
        }
        default: {
          return '';
        }
      }
    };

    const itemImage = getImage({ name: item, field: 'icon' });

    const openBoostProduction = (): void => {
      if (!resourceList.includes(plant as ResourceTypeText)) {
        openModal({
          renderKind: 'boostProduction',
          props: {
            plantId: plantId,
            material: item as PlantTypeText | GoodsType,
          },
        });
      }
    };

    return (
      <div
        className={cn(
          'w-full flex flex-col h-full px-4 pt-4 pb-10 modal_shadow relative',
          css.gradient,
        )}
      >
        <div className="absolute left-4 -top-16 z-[2]">
          <HelpIcon />
        </div>

        <div className="flex items-center mb-4 gap-2 isolate">
          <H3
            weight="bold"
            className={cn(
              totalBoost ? '!text-accent' : '!text-white/40',
              'mr-auto',
            )}
          >
            {`${t(totalBoost ? 'Total Boost' : 'No Active Boost')}${totalBoost ? ' x' + totalBoost : ''}`}
          </H3>
          <CoinBalanceTopUp
            className="!h-12 !pl-2 !pr-3 py-[0.625rem]"
            contenctClassName="!items-start"
            compare={Infinity}
            iconSize="1.75rem"
          />
          <Close onClick={closeModal} imageVariant="modal" variant="modal" />
        </div>
        {plantType !== 'Quarry' && !!boostProductionDynamicData?.inPool && (
          <div className="w-full h-14 p-2 rounded-lg bg-white/10 flex items-center gap-4 mb-4">
            <div className="w-10 h-10 rounded-lg bg-white/10">
              <Picture img={itemImage?.img} webp={itemImage?.webp} alt={item} />
            </div>
            <div className="flex flex-col h-full justify-between flex-1">
              <StringSmall>{t('Instant production')}</StringSmall>
              <H2 weight="bold" className="flex items-end">
                <span className="mr-1">
                  {boostProductionDynamicData?.inPool}
                </span>
                <StringXSmall weight="regular" className="!text-white/40">
                  {t('in pool')}
                </StringXSmall>
              </H2>
            </div>
            <div className="w-[7.5rem] h-8">
              <PurpleSmButton
                onClick={openBoostProduction}
                className="!h-full flex items-center gap-1"
              >
                <CurrencyImage kind="tgem" size="1.5rem" />
                <StringMain weight="extrabold" className={cn('huge-text')}>
                  {boostProductionDynamicData?.price}
                </StringMain>
              </PurpleSmButton>
            </div>
          </div>
        )}
        <div className="w-full flex flex-col">
          <StringMain weight="medium" className="mb-2">
            {getBoostDescription(plantType)}
          </StringMain>
          <div className={cn(css.boostCards, 'mb-6')}>
            {forAllPlantlist.map((props, index) => (
              <BoostCard
                key={index}
                {...props}
                resource={item}
                plantType={plantType}
                plantId={plantId}
              />
            ))}
          </div>
          <StringMain weight="medium" className="mb-2">
            {getText({ name: plant, kind: 'build', t })}
          </StringMain>
          <div className={css.boostCards}>
            {forCurrentPlantList.map((props, index) => (
              <BoostCard
                key={index}
                {...props}
                resource={item}
                plantType={plantType}
                plantId={plantId}
              />
            ))}
          </div>
        </div>
      </div>
    );
  },
);

export { BoostCardList };
